import type { ScheduleType } from '@repo/api-codegen';
import {
	listAiTaskEmbeddedPromptsQueryKey,
	listPromptsQueryKey,
} from '@repo/api-codegen';
import cronstrue from 'cronstrue';
import { isNumber } from 'lodash-es';
import { queryClient } from '../../api';
import { aiEmbeddedQueryKeyFactory } from '../../api/hooks/ai/constants';
import { toAmPm } from '../../utils/time';
import { DAYS_OF_WEEK, HOURS_OF_DAY } from './constants';

export function getHumanReadableSchedule(
	scheduleType: ScheduleType,
	cronExpression: string,
	dayOfWeek: number | undefined = undefined,
	hourOfDay: number | undefined = undefined
) {
	if (scheduleType === 'hourly') {
		return {
			humanReadableSchedule: 'Agent will run every hour',
			invalidCronExpression: false,
		};
	} else if (scheduleType === 'daily') {
		return {
			humanReadableSchedule: `Agent will run every day at ${HOURS_OF_DAY[hourOfDay ?? 0].label}`,
			invalidCronExpression: false,
		};
	} else if (
		scheduleType === 'weekly' &&
		isNumber(dayOfWeek) &&
		isNumber(hourOfDay)
	) {
		return {
			humanReadableSchedule: `Agent will run every ${DAYS_OF_WEEK[dayOfWeek].label} at ${HOURS_OF_DAY[hourOfDay].label}`,
			invalidCronExpression: false,
		};
	} else if (scheduleType === 'cron' && cronExpression) {
		try {
			const cronString = cronstrue.toString(cronExpression);
			const formattedCronString =
				cronString.charAt(0).toLowerCase() + cronString.slice(1);

			return {
				humanReadableSchedule: `Agent will run ${formattedCronString}`,
				invalidCronExpression: false,
			};
		} catch {
			// nothing
		}
	}

	return {
		humanReadableSchedule: 'Invalid cron expression',
		invalidCronExpression: true,
	};
}

/**
 * Returns a concise, human-friendly schedule label for agent list items.
 * Examples:
 * - "Hourly"
 * - "Daily at 10:30am"
 * - "Weekly on Monday at 9:00am"
 * - Fallbacks to a condensed cronstrue string when complex cron expressions are used
 */
export function getAgentListItemHumanReadableSchedule(
	cronExpression?: string | null
): string {
	if (!cronExpression) {
		return 'On demand';
	}

	const cron = cronExpression.trim();
	const parts = cron.split(/\s+/);

	// Basic 5-part cron parsing: m h dom mon dow
	if (parts.length >= 5) {
		const [min, hour, dom, mon, dow] = parts;

		const isStar = (v: string) => v === '*' || v === '?';
		const isNumeric = (v: string) => /^\d+$/.test(v);

		// Hourly: specific minute, every hour
		if (
			isNumeric(min) &&
			isStar(hour) &&
			isStar(dom) &&
			isStar(mon) &&
			isStar(dow)
		) {
			return 'Hourly';
		}

		// Daily at HH:MM
		if (
			isNumeric(min) &&
			isNumeric(hour) &&
			isStar(dom) &&
			isStar(mon) &&
			isStar(dow)
		) {
			return `Daily at ${toAmPm(hour, min)}`;
		}

		// Weekly on Day at HH:MM (simple single day-of-week)
		if (
			isNumeric(min) &&
			isNumeric(hour) &&
			isStar(dom) &&
			isStar(mon) &&
			!isStar(dow) &&
			/^\d$/.test(dow)
		) {
			const dayIndex = parseInt(dow, 10);
			const dayLabel = DAYS_OF_WEEK[dayIndex]?.label ?? 'Unknown day';
			return `Weekly on ${dayLabel} at ${toAmPm(hour, min)}`;
		}
	}

	// Fallback: derive from cronstrue, then condense common prefixes
	try {
		const full = cronstrue.toString(cronExpression);
		// Normalize casing and spacing to match target examples
		const lowered = full.replace(/\s(AM|PM)/g, (m) =>
			m.toLowerCase().replace(' ', '')
		);

		// Map patterns
		if (/^Every hour/i.test(lowered)) {
			return 'Hourly';
		}
		const dailyMatch = lowered.match(/^Every day at (.+)$/i);
		if (dailyMatch) {
			return `Daily at ${dailyMatch[1]}`;
		}
		const weeklyMatch = lowered.match(
			/^Every (Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday) at (.+)$/i
		);
		if (weeklyMatch) {
			const [, day, time] = weeklyMatch;
			return `Weekly on ${day} at ${time}`;
		}
		return lowered;
	} catch {
		return 'Scheduled';
	}
}

export async function invalidateChatHistoryCache(): Promise<void> {
	await Promise.all([
		queryClient.invalidateQueries({
			queryKey: aiEmbeddedQueryKeyFactory.allLists(),
		}),
		queryClient.invalidateQueries({
			queryKey: listPromptsQueryKey({}),
		}),
	]);
}

export async function invalidateAgentRunsCache(taskId: string): Promise<void> {
	await queryClient.invalidateQueries({
		queryKey: listAiTaskEmbeddedPromptsQueryKey({
			pathParams: { taskId },
			queryParams: {},
		}),
	});
}
